import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingBag, CheckCircle, ExternalLink } from 'lucide-react';

// Delivery bag expression of interest, inside the driver portal so it isn't a link in a
// text that gets glanced over. Same backend as the public page (/delivery-bag): the portal
// cookie identifies the driver, so there's no PIN to type here.
//
//   variant 'home' — on My Shift. Shows until the driver answers, then a one-line thanks,
//                    and is gone on the next visit.
//   variant 'docs' — top of My Docs. Stays while replies are open so an answer can change.
//
// Hides itself entirely once replies close (the server decides, not the phone's clock).
const API = '/.netlify/functions/bag-eoi-sb';
const post = (body) => fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json());

// One small read for the My Docs red dot, so it shows before either card has mounted.
export async function bagEoiPending() {
  try {
    const d = await post({ action: 'status' });
    return d && d.ok && !d.closed && d.session && !d.session.answered ? 1 : 0;
  } catch { return 0; }
}

export default function BagEoiCard({ variant = 'home', onPendingChange }) {
  const [st, setSt] = useState(null);         // status response
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);       // { ok, text }
  const [justAnswered, setJustAnswered] = useState(false);

  const report = useCallback((n) => { if (onPendingChange) onPendingChange(n); }, [onPendingChange]);

  useEffect(() => {
    let alive = true;
    post({ action: 'status' }).then(d => {
      if (!alive || !d || !d.ok) return;
      setSt(d);
      if (d.session && d.session.qty > 0) setQty(d.session.qty);
      report(!d.closed && d.session && !d.session.answered ? 1 : 0);
    }).catch(() => {});
    return () => { alive = false; };
  }, [report]);

  if (!st || st.closed || !st.session) return null;
  const answered = st.session.answered;
  const mine = st.session.qty;
  if (variant === 'home' && answered && !justAnswered) return null;

  const answer = async (n) => {
    setBusy(true); setMsg(null);
    try {
      const d = await post({ action: 'register', qty: n });
      if (!d.ok) { setMsg({ ok: false, text: d.error || 'Something went wrong. Please try again.' }); return; }
      setSt(s => ({ ...s, totalBags: d.totalBags, drivers: d.drivers, session: { ...s.session, qty: d.qty, answered: true } }));
      setJustAnswered(true);
      report(0);
      setMsg({ ok: true, text: d.qty > 0
        ? `Thanks, you're down for ${d.qty} bag${d.qty === 1 ? '' : 's'}. We'll confirm the final price once the order is placed.`
        : "Thanks, we've noted you're not interested. You can change your answer in My Docs until Thursday 24 September." });
    } catch {
      setMsg({ ok: false, text: "Couldn't reach the server. Check your signal and try again." });
    } finally { setBusy(false); }
  };

  const pct = Math.min(100, Math.round((st.totalBags / (st.minimum || 100)) * 100));
  const btn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 14px', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 800, fontSize: '0.86rem', fontFamily: 'inherit' };

  // After answering on My Shift, collapse to the thanks line — the question is done.
  if (variant === 'home' && justAnswered && msg && msg.ok) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', marginBottom: 16, borderRadius: 'var(--radius)', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.4)', fontSize: '0.82rem', color: 'var(--hp-text)' }}>
        <CheckCircle size={16} color="#22c55e" style={{ flexShrink: 0 }} /> {msg.text}
      </div>
    );
  }

  return (
    <div style={{ width: '100%', background: 'rgba(201,168,76,0.08)', border: '1px solid var(--hp-gold)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: 'var(--hp-gold)', fontSize: '0.78rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        <ShoppingBag size={15} /> Delivery bag · {answered ? 'your answer' : 'are you interested?'}
      </div>

      <img src="/delivery-bag.jpg" alt="The quoted delivery bag: large black box-shaped bag with carry handles"
        style={{ display: 'block', width: '100%', maxHeight: 170, objectFit: 'cover', borderRadius: 8, marginTop: 10, background: '#fff' }} />

      <div style={{ fontSize: '0.86rem', color: 'var(--hp-text)', lineHeight: 1.5, marginTop: 10 }}>
        A large box bag (57 × 46 × 77 cm, about 200 litres) with carry handles. <b>Approx. $44 inc GST</b>, deducted from
        your RCTI once you have the bag. We need <b>100 bags</b> to place the order. Not a commitment to buy.
      </div>
      <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 4 }}>Replies close Thursday 24 September.</div>

      <div style={{ height: 8, borderRadius: 999, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', overflow: 'hidden', marginTop: 10 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--hp-gold)' }} />
      </div>
      <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 4 }}>{st.totalBags} of {st.minimum} bags so far</div>

      {answered && !msg && (
        <div style={{ fontSize: '0.84rem', fontWeight: 700, marginTop: 10, color: 'var(--hp-text)' }}>
          {mine > 0 ? `You're down for ${mine} bag${mine === 1 ? '' : 's'}.` : "You said you're not interested."}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={qty} onChange={e => setQty(parseInt(e.target.value, 10))} disabled={busy} aria-label="How many bags"
          style={{ padding: '9px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)', fontSize: '0.9rem', fontFamily: 'inherit' }}>
          {Array.from({ length: 10 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n} bag{n === 1 ? '' : 's'}</option>)}
        </select>
        <button disabled={busy} onClick={() => answer(qty)}
          style={{ ...btn, flex: '1 1 150px', border: 'none', background: 'var(--hp-gold)', color: '#1a1a2e', opacity: busy ? 0.6 : 1 }}>
          {answered && mine > 0 ? 'Update' : "Yes, I'm interested"}
        </button>
        <button disabled={busy || (answered && mine === 0)} onClick={() => answer(0)}
          style={{ ...btn, flex: '1 1 120px', background: 'transparent', border: '1px solid var(--hp-border)', color: 'var(--hp-text)', opacity: busy || (answered && mine === 0) ? 0.5 : 1 }}>
          No, not interested
        </button>
      </div>

      {msg && (
        <div style={{ marginTop: 10, padding: '9px 12px', borderRadius: 8, fontSize: '0.82rem', lineHeight: 1.45,
          background: msg.ok ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', color: msg.ok ? 'var(--hp-text)' : 'var(--hp-red)' }}>
          {msg.text}
        </div>
      )}

      <a href="/delivery-bag" target="_blank" rel="noreferrer"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 10, fontSize: '0.8rem', fontWeight: 700, color: 'var(--hp-gold)', textDecoration: 'none' }}>
        Full details: benefits, cost, safety <ExternalLink size={12} />
      </a>
    </div>
  );
}
